
import {defineComponent} from 'vue';
import animateScrollTo from 'animated-scroll-to';
import {useStoreComposition} from "@/composables/StoreComposition";
import {useGeolocationComposition} from "@/composables/GeolocationComposition";
import {useComponentDimensions} from "@/composables/DimensionsComposition";
import LocationFilter from "@/components/LocationFilter.vue";
import LocationSearch from "@/components/LocationSearch.vue";
import StoreListItem from "@/components/StoreListItem.vue";
import {configuration} from "@/composables/ConfigurationComposition";
import {useTranslationComposition} from "@/composables/TranslationComposition";

export default defineComponent({
  components: {
    LocationFilter,
    LocationSearch,
    StoreListItem
  },
  setup() {
    /**
     * handle computed properties
     */
    const {
      hasAllStores,
      hasRestrictedStores,
      hasNoStores,
      hasNoSuggestResults,
      hasNoSuggestSelection
    } = useStoreComposition;

    /**
     * handle computed properties
     */
    const {
      infoNoStoresTranslation,
      noSuggestResultsTranslation,
      infoRestrictedStoresTranslation,
      infoAllStoresTranslation
    } = useTranslationComposition;

    return {
      hasAllStores,
      hasRestrictedStores,
      hasNoStores,
      hasNoSuggestResults,
      hasNoSuggestSelection,
      infoNoStoresTranslation,
      noSuggestResultsTranslation,
      infoRestrictedStoresTranslation,
      infoAllStoresTranslation,
      dimensionComposition: useComponentDimensions,
      geolocationComposition: useGeolocationComposition,
      storeComposition: useStoreComposition,
      translationComposition: useTranslationComposition
    };
  },
  computed: {
    isFilterEnabled() {
      return configuration.enableFilter;
    },
    isCenteredMapCSSClass() {
      return configuration.isCenteredMap ? '' : 'not-centered';
    },
    resultTypes() {
      return configuration.resultTypes ?? [];
    }
  },
  methods: {
    /**
     * activate store when "opened" in list
     *
     * @param store
     */
    onActivate({store}) {
      const activeStore = this.storeComposition.activeStore.value;

      if (store !== null) {
        if (activeStore && activeStore !== store) {
          activeStore.isActive = false;
        }

        store.isActive = true;
      }
    },
    /**
     * set store inactive when "closed" in list
     *
     * @param store
     */
    onDeactivate({store}) {
      const activeStore = this.storeComposition.activeStore.value;

      if (activeStore && activeStore === store) {
        activeStore.isActive = false;
      }
    },
    /**
     * handle search submit
     */
    onSearchSubmit() {
      const filterComponent = (this.$refs.filter as typeof LocationFilter);

      // TODO: may use composition api instead of accessing sibling / children
      if (filterComponent !== undefined) {
        filterComponent.open()
      }
    },
    /**
     * handle geolocation change
     */
    onGeolocationChange() {
      const filterComponent = (this.$refs.filter as typeof LocationFilter);

      // TODO: may use composition api instead of accessing sibling / children
      if (filterComponent !== undefined) {
        filterComponent.open()
      }
    },
    /**
     * close filter on item open and scroll to opened item in list
     *
     * @param listitemElement
     */
    onOpen(listitemElement: HTMLElement) {
      const filterComponent = (this.$refs.filter as typeof LocationFilter);

      // TODO: may use composition api instead of accessing sibling / children
      if (filterComponent !== undefined) {
        filterComponent.close()
      }

      setTimeout(() => {
        const mediaQuery = window.matchMedia('(max-width: 768px)');

        if (mediaQuery.matches) {
          animateScrollTo(listitemElement, {
            verticalOffset: -this.dimensionComposition.mapElementHeight
          });
        } else {
          animateScrollTo(listitemElement, {
            elementToScroll: (this.$refs.scroll as HTMLElement)
          });
        }
      }, 250);
    }
  },
  mounted() {
    this.dimensionComposition.listElement = this.$refs.listElement as HTMLElement;
    this.dimensionComposition.listInnerElement = this.$refs.listInnerElement as HTMLElement;

    if (configuration.loadInitially === true && configuration.enableFilter === false) {
      this.storeComposition.loadStores();
    }
  }
});
